import { useState, useEffect, useCallback } from 'react';
import {
    fetchAiModels, refreshAiModels, addAiModel, updateAiModel, assignAiTask,
} from '../../api/aiModels';
import { describeRefresh } from './aiModels';

// State and actions of the AI models section. Every change reloads the
// catalog afterwards so `assigned_tasks` and the task table stay in step.
export default function useAiModels({ token }) {
    const [models, setModels] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(''); // 'refresh' | 'add' | `model-${id}` | `task-${task}`
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const load = useCallback(async () => {
        const data = await fetchAiModels(token);
        setModels(data.models);
        setTasks(data.tasks);
    }, [token]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                await load();
            } catch (err) {
                if (!cancelled) setError(err.message || 'Failed to load the model catalog.');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [load]);

    // Run one change: clear old feedback, mark the row busy, reload on success.
    const change = useCallback(async (label, action) => {
        setBusy(label);
        setError('');
        setMessage('');
        try {
            const result = await action();
            await load();
            return result;
        } catch (err) {
            setError(err.message || 'Something went wrong.');
            return null;
        } finally {
            setBusy('');
        }
    }, [load]);

    const refresh = useCallback(() => change('refresh', async () => {
        const report = await refreshAiModels(token);
        setMessage(describeRefresh(report));
        return report;
    }), [change, token]);

    const add = useCallback((payload) => change('add', () => addAiModel(token, payload)), [change, token]);

    const update = useCallback(
        (model, patch) => change(`model-${model.id}`, () => updateAiModel(token, model.id, patch)),
        [change, token],
    );

    const assign = useCallback(
        (task, aiModelId) => change(`task-${task}`, () => assignAiTask(token, task, aiModelId)),
        [change, token],
    );

    return { models, tasks, loading, busy, error, message, refresh, add, update, assign };
}
